import { useState } from "react";
import { Alert, Button, Card, Descriptions, PasswordInput, Space, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { PasswordChecklist, passwordMeetsPolicy } from "../auth/PasswordChecklist.js";
import { useErrorText } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { SecurityChecklist, type SecurityCheck } from "./SecurityChecklist.js";
import type { SecurityOverview } from "../types.js";

/**
 * Security tab: what is still open (the checklist), then the one control that
 * lives here — the password. Changing it revokes every session server-side, so
 * the caller signs the user out and sends them back to the sign-in page.
 */

export function SecurityTab({
  checks,
  overview,
  onPasswordChanged,
}: {
  checks: SecurityCheck[];
  overview: SecurityOverview | null;
  onPasswordChanged: () => Promise<void>;
}) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const fmtDate = useFormatDateTime();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [changing, setChanging] = useState(false);

  const mismatch = confirm !== "" && next !== confirm;
  const canSubmit = current !== "" && passwordMeetsPolicy(next) && next === confirm;

  const changePassword = async () => {
    setChanging(true);
    try {
      await api<void>("/auth/password", {
        method: "POST",
        body: { currentPassword: current, newPassword: next },
      });
      toast.success(t("profile.passwordChanged"));
      await onPasswordChanged();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setChanging(false);
    }
  };

  return (
    <Space direction="vertical" size="lg" className="w-full">
      <Card title={t("profile.checklist")}>
        <SecurityChecklist checks={checks} />
      </Card>

      <Card title={t("profile.security")}>
        <Space direction="vertical" size="md" className="w-full">
          {overview && (
            <Descriptions
              column={2}
              size="small"
              items={[
                {
                  key: "changed",
                  label: t("profile.passwordAge"),
                  children: fmtDate(overview.passwordChangedAt),
                },
                {
                  key: "expires",
                  label: t("profile.passwordExpires"),
                  children: overview.passwordExpiresAt
                    ? fmtDate(overview.passwordExpiresAt)
                    : t("profile.passwordNoPolicy"),
                },
              ]}
            />
          )}

          {/* Every session is revoked on success — say so before they commit. */}
          <Alert type="info">{t("profile.passwordChangeWarning")}</Alert>

          <div className="pf-form">
            <PasswordInput
              label={t("profile.currentPassword")}
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            <div>
              <PasswordInput
                label={t("profile.newPassword")}
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              {next !== "" && <PasswordChecklist value={next} />}
            </div>
            <PasswordInput
              label={t("profile.confirmNewPassword")}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              {...(mismatch ? { error: t("profile.passwordMismatch") } : {})}
            />
            <div>
              <Button
                variant="primary"
                size="sm"
                loading={changing}
                disabled={!canSubmit}
                onClick={() => void changePassword()}
              >
                {t("profile.changePassword")}
              </Button>
            </div>
          </div>
        </Space>
      </Card>
    </Space>
  );
}
